/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/14/22
 */

import React from "react";
import ViewportEvent, {eventSx} from "./ViewportEvent";
import {Employee, WorkdayEvent} from "../interfaces/Global";
import {getEventType} from "./event-types";
import {addDays, differenceInHours, formatDistance} from "date-fns";
import {
	Card,
	CardContent,
	CardHeader, Stack,
	Typography, useTheme
} from "@mui/material";
import ModelView, {AttributeConfig} from "../components/ModelView";
import Avatar from "../components/Avatar";
import ALIcon from "@mui/icons-material/BeachAccess";
import TOIcon from "@mui/icons-material/PowerSettingsNew";
import {utcToZonedTime} from "date-fns-tz";
import {format} from "../dates-helper";

export interface WorkdayViewportEventProps {
	viewportRange: Interval
	rangeResolution: number
	employee: Employee
	event: WorkdayEvent
}

export default function WorkdayViewportEvent(props: WorkdayViewportEventProps) {

	const eventType = getEventType(props.event.typeId);
	const theme = useTheme();

	return (
		<ViewportEvent
			viewportRange={props.viewportRange}
			rangeResolution={props.rangeResolution}
			normalizeTimezone={props.employee.timeZone}
			event={{
				title: eventType?.name || "Calendar Event",
				typeId: eventType?.id || "AL",
				startDatetime: new Date(props.event.startDatetime),
				endDatetime: new Date(props.event.endDatetime)
			}}
			tooltip={
				<EventTooltip
					event={props.event}
					employee={props.employee}
				/>
			}
			details={
				<EventDetails
					event={props.event}
					employee={props.employee}
				/>
			}
			viewportIcon={eventType?.id === "AL" ? <ALIcon fontSize={"small"} /> : <TOIcon fontSize={"small"} />}
			getViewportLabel={(width) => {
				if (!eventType) return null;
				let label = eventType.name;
				if (width <= 40) {
					return null;
				} else if (width <= 80) {
					label = eventType.id;
				} else if (width > 200) {
					label += " (" + formatDistance(props.event.endDatetime, props.event.startDatetime) + ")";
				}
				return label;
			}}
			sx={{
				...eventSx(theme, eventType?.parent?.id?.toLowerCase(), false),
				...eventSx(theme, props.event.typeId?.toLowerCase(), false),
				zIndex: 30
			}}
		/>
	);

}

interface EventTooltipProps {
	event: WorkdayEvent
	employee: Employee
}

function EventTooltip({ event, employee }: EventTooltipProps) {

	const eventType = getEventType(event.typeId);
	const diffInHours = differenceInHours(event.endDatetime, event.startDatetime);
	const eStartDate = utcToZonedTime(event.startDatetime, employee.timeZone);
	const eEndDate = utcToZonedTime(event.endDatetime, employee.timeZone);

	return (
		<Stack>
			<div>{employee.displayName}</div>
			<Typography>Workday Event</Typography>
			<Stack>{eventType?.name}</Stack>
			{diffInHours === 24 && <Stack>{format(eStartDate, 'PPPP')}</Stack>}
			{diffInHours !== 24 && <>
				<Stack>First Day: {format(eStartDate, 'PPPP')}</Stack>
				<Stack>Last Day: {format(addDays(eEndDate, -1), 'PPPP')}</Stack>
			</>}
			<Stack>{formatDistance(eEndDate, eStartDate)}</Stack>
		</Stack>
	)

}

interface EventDetailsProps {
	event: WorkdayEvent
	employee: Employee
}

function EventDetails({ event, employee }: EventDetailsProps) {

	const eventType = getEventType(event.typeId);

	const attributes: AttributeConfig<WorkdayEvent>[] = [
		{ label: "Source", value: "Workday" },
		{ label: "Type", value: eventType?.name },
		{ label: "Category", value: eventType?.parent?.name },
		{ attribute: "startDatetime", format: "datetime", label: "Start Date" },
		{ attribute: "endDatetime", format: "datetime", label: "End Date" },
	];

	return (
		<Card>
			<CardHeader
				avatar={<Avatar id={employee.id as string} />}
				title={"Workday Event"}
				subheader={employee.displayName}
			/>
			<CardContent>
				<ModelView
					attributes={attributes}
					model={event}
					labelSx={{
						pr: 1,
						width: 130,
						textAlign: "right"
					}}
					valueSx={{
						maxWidth: 500,
						maxHeight: 200,
						overflow: "auto"
					}}
					nullTypographyProps={{
						variant: "body2"
					}}
					labelTypographyProps={{
						color: "textSecondary",
						variant: "body2"
					}}
					valueTypographyProps={{
						variant: "body2"
					}}
				/>
			</CardContent>
		</Card>
	);

}